import { COMMUNES, communesForPostalCode, findCommune, isListedPostalArea } from '@clim/domain';
import { useId } from 'react';
import { Field, Input } from './ui';

/** « Baie-Mahault (97122) » : une option par commune et par code postal, triées par nom */
const OPTIONS = COMMUNES.flatMap((c) => c.postalCodes.map((cp) => ({ label: `${c.name} (${cp})`, name: c.name, cp }))).sort((a, b) =>
  a.label.localeCompare(b.label, 'fr'),
);

/**
 * Code postal + commune. Aux Antilles et en Guyane, la commune se choisit dans la liste officielle (pas de faute
 * d'orthographe) : choisir la commune remplit le code postal, taper le code postal remplit la commune. Ailleurs, saisie
 * libre.
 */
export function CommuneFields({
  postalCode,
  city,
  onChange,
  errors = {},
  labels = { postalCode: 'Code postal', city: 'Commune' },
}: {
  postalCode: string | null | undefined;
  city: string | null | undefined;
  onChange(v: { postalCode: string; city: string }): void;
  errors?: { postalCode?: string; city?: string };
  labels?: { postalCode: string; city: string };
}) {
  const listId = useId();
  const cp = postalCode ?? '';
  const name = city ?? '';
  const forCp = communesForPostalCode(cp);
  const options = forCp.length ? OPTIONS.filter((o) => o.cp === cp.replace(/\s/g, '')) : OPTIONS;
  const listed = isListedPostalArea(cp) || !!findCommune(name);
  const unknown = !!name && isListedPostalArea(cp) && !findCommune(name, cp);
  const onCp = (v: string) => {
    const next = communesForPostalCode(v);
    // un seul choix possible : la commune est remplie ; sinon on garde celle saisie si elle correspond
    const keep = findCommune(name, v);
    onChange({ postalCode: v, city: next.length === 1 ? next[0]!.name : keep ? keep.name : next.length ? '' : name });
  };
  const onCity = (v: string) => {
    const picked = OPTIONS.find((o) => o.label === v);
    if (picked) return onChange({ postalCode: picked.cp, city: picked.name });
    const hit = findCommune(v, forCp.length ? cp : null) ?? null;
    if (hit && hit.name === v) {
      const nextCp = hit.postalCodes.includes(cp.replace(/\s/g, '')) ? cp : hit.postalCodes.length === 1 ? hit.postalCodes[0]! : '';
      return onChange({ postalCode: nextCp, city: hit.name });
    }
    onChange({ postalCode: cp, city: v });
  };
  return (
    <>
      <Field label={labels.postalCode} error={errors.postalCode}>
        <Input aria-label={labels.postalCode} value={cp} onChange={(e) => onCp(e.target.value)} inputMode="numeric" autoComplete="postal-code" />
      </Field>
      <Field
        label={labels.city}
        error={errors.city ?? (unknown ? 'Choisissez la commune dans la liste' : undefined)}
        hint={listed || !name ? 'Tapez le début du nom puis choisissez dans la liste' : undefined}
      >
        <Input aria-label={labels.city} list={listId} value={name} onChange={(e) => onCity(e.target.value)} autoComplete="off" />
        <datalist id={listId}>
          {options.map((o) => (
            <option key={o.label} value={o.label} />
          ))}
        </datalist>
      </Field>
    </>
  );
}
